export const PROGRAMME_STATUS_LABELS = {
  planned: "Planned",
  ready_to_start: "Ready to start",
  active: "Active",
  paused: "Paused",
  extended: "Extended",
  awaiting_review: "Awaiting review",
  final_assessment_due: "Final assessment due",
  completed: "Completed",
  cancelled: "Cancelled",
  archived: "Archived",
};

export const PROGRAMME_STATUS_COLORS = {
  planned: "bg-slate-100 text-slate-700 border-transparent",
  ready_to_start: "bg-blue-50 text-blue-700 border-transparent",
  active: "bg-ukgreen-light text-ukgreen border-transparent",
  paused: "bg-amber-100 text-amber-800 border-transparent",
  extended: "bg-purple-50 text-purple-700 border-transparent",
  awaiting_review: "bg-blue-50 text-blue-700 border-transparent",
  final_assessment_due: "bg-indigo-50 text-indigo-700 border-transparent",
  completed: "bg-ukgreen-light text-ukgreen border-transparent",
  cancelled: "bg-slate-100 text-slate-500 border-transparent",
  archived: "bg-slate-100 text-slate-500 border-transparent",
};

export const WEEK_OPTIONS = [8, 10, 12, 14, 16];

export const TRAINING_DAYS = [
  { value: "mon", label: "Mon" }, { value: "tue", label: "Tue" }, { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" }, { value: "fri", label: "Fri" },
];

export const OBJECTIVE_CATEGORIES = [
  { value: "road_safety", label: "Road Safety" },
  { value: "route_recognition", label: "Route Recognition" },
  { value: "public_transport", label: "Public Transport" },
  { value: "travel_card", label: "Travel Card / Oyster" },
  { value: "stranger_awareness", label: "Stranger Awareness" },
  { value: "communication", label: "Communication" },
  { value: "emergency_response", label: "Emergency Response" },
  { value: "problem_solving", label: "Problem Solving" },
  { value: "confidence", label: "Confidence" },
  { value: "mobile_phone_skills", label: "Mobile Phone Skills" },
  { value: "dealing_with_change", label: "Dealing with Change" },
  { value: "personal_safety", label: "Personal Safety" },
  { value: "independence", label: "Independence" },
  { value: "other", label: "Other" },
];

export const OBJECTIVE_STATUS_LABELS = {
  not_started: "Not started",
  in_progress: "In progress",
  achieved: "Achieved",
  partially_achieved: "Partially achieved",
  requires_further_training: "Requires further training",
  no_longer_applicable: "No longer applicable",
};

export const OBJECTIVE_STATUS_COLORS = {
  not_started: "bg-slate-100 text-slate-600 border-transparent",
  in_progress: "bg-blue-50 text-blue-700 border-transparent",
  achieved: "bg-ukgreen-light text-ukgreen border-transparent",
  partially_achieved: "bg-amber-100 text-amber-800 border-transparent",
  requires_further_training: "bg-red-50 text-red-700 border-transparent",
  no_longer_applicable: "bg-slate-100 text-slate-500 border-transparent",
};

export const PRIORITIES = [
  { value: "high", label: "High" },
  { value: "standard", label: "Standard" },
  { value: "low", label: "Low" },
];

export const SKILL_AREAS = [
  { key: "road_safety", label: "Road Safety" },
  { key: "route_recognition", label: "Route Recognition" },
  { key: "public_transport", label: "Public Transport" },
  { key: "travel_card", label: "Travel Card" },
  { key: "stranger_awareness", label: "Stranger Awareness" },
  { key: "communication", label: "Communication" },
  { key: "emergency_response", label: "Emergency Response" },
  { key: "problem_solving", label: "Problem Solving" },
  { key: "overall_journey", label: "Overall Journey" },
];

export const DIRECTIONS = [
  { value: "outbound", label: "Home → School", short: "Home → School" },
  { value: "return", label: "School → Home", short: "School → Home" },
  { value: "other", label: "Other authorised training journey", short: "Other" },
];

export const TRANSPORT_OPTIONS = [
  { value: "walk", label: "Walking" },
  { value: "bus", label: "Bus" },
  { value: "train", label: "Train" },
  { value: "underground", label: "Underground" },
  { value: "tram", label: "Tram" },
  { value: "taxi", label: "Taxi" },
  { value: "other", label: "Other" },
];

export const ATTENDANCE_OPTIONS = [
  { value: "completed", label: "Completed", group: "completed" },
  { value: "child_absent", label: "Child absent", group: "not_completed" },
  { value: "child_refused", label: "Child refused", group: "not_completed" },
  { value: "trainer_unavailable", label: "Trainer unavailable", group: "not_completed" },
  { value: "school_closed", label: "School closed", group: "not_completed" },
  { value: "transport_disruption", label: "Transport disruption", group: "not_completed" },
  { value: "cancelled_by_parent", label: "Cancelled by parent/carer", group: "not_completed" },
  { value: "cancelled_by_school", label: "Cancelled by school", group: "not_completed" },
  { value: "cancelled_by_deluxe", label: "Cancelled by Deluxe", group: "not_completed" },
  { value: "other", label: "Other", group: "not_completed" },
];

export const ATTENDANCE_LABELS = Object.fromEntries(ATTENDANCE_OPTIONS.map((o) => [o.value, o.label]));

export const RESPONSE_OPTIONS = [
  { value: "independent", label: "Independent", short: "IND" },
  { value: "minimal_prompt", label: "Minimal prompt", short: "MIN" },
  { value: "verbal_prompt", label: "Verbal prompt", short: "VP" },
  { value: "visual_prompt", label: "Visual prompt", short: "VIS" },
  { value: "physical_support", label: "Physical / active support", short: "PHYS" },
  { value: "intervention_required", label: "Trainer intervention required", short: "INT" },
  { value: "not_demonstrated", label: "Not demonstrated", short: "ND" },
  { value: "not_assessed", label: "Not assessed", short: "NA" },
  { value: "not_applicable", label: "Not applicable", short: "—" },
];

export const RESPONSE_LABELS = Object.fromEntries(RESPONSE_OPTIONS.map((o) => [o.value, o.label]));

export const PERFORMANCE_AREAS = [
  {
    key: "road_safety", label: "Road Safety",
    items: [
      { key: "stops_before_crossing", label: "Stops appropriately before crossing" },
      { key: "looks_listens", label: "Looks / listens appropriately" },
      { key: "identifies_crossing", label: "Identifies suitable crossing" },
      { key: "uses_crossing", label: "Uses crossings appropriately" },
      { key: "attentive_near_roads", label: "Remains attentive near roads" },
      { key: "responds_to_safety_instruction", label: "Responds to trainer safety instruction" },
    ],
  },
  {
    key: "route_recognition", label: "Route Recognition",
    items: [
      { key: "identifies_route", label: "Identifies route" },
      { key: "recognises_landmarks", label: "Recognises landmarks" },
      { key: "identifies_direction", label: "Identifies correct direction" },
      { key: "identifies_stop", label: "Identifies correct stop / station" },
      { key: "recognises_destination", label: "Recognises destination" },
    ],
  },
  {
    key: "public_transport", label: "Public Transport",
    items: [
      { key: "identifies_service", label: "Identifies correct service" },
      { key: "waits_safely", label: "Waits safely" },
      { key: "boards_correctly", label: "Boards correctly" },
      { key: "behaves_appropriately", label: "Behaves appropriately during journey" },
      { key: "monitors_journey", label: "Monitors journey" },
      { key: "identifies_destination", label: "Identifies destination" },
      { key: "exits_safely", label: "Exits safely" },
    ],
  },
  {
    key: "travel_card", label: "Travel Card",
    items: [
      { key: "manages_card", label: "Manages card" },
      { key: "taps_correctly", label: "Taps / presents correctly" },
      { key: "keeps_card_secure", label: "Keeps card secure" },
      { key: "responds_to_card_problems", label: "Responds appropriately to card problems" },
    ],
  },
  {
    key: "stranger_awareness", label: "Personal / Stranger Safety",
    items: [
      { key: "maintains_boundaries", label: "Maintains appropriate boundaries" },
      { key: "responds_to_unfamiliar_people", label: "Responds appropriately to unfamiliar people" },
      { key: "identifies_help", label: "Identifies appropriate people for help" },
      { key: "protects_personal_info", label: "Protects personal information" },
    ],
  },
  {
    key: "communication", label: "Communication",
    items: [
      { key: "communicates_needs", label: "Communicates needs" },
      { key: "asks_for_help", label: "Asks for help" },
      { key: "follows_instructions", label: "Follows appropriate instructions" },
      { key: "communicates_problems", label: "Communicates problems" },
    ],
  },
  {
    key: "emergency_response", label: "Emergency / Problem Solving",
    items: [
      { key: "route_changes", label: "Understands what to do if route changes" },
      { key: "if_lost", label: "Understands what to do if lost" },
      { key: "knows_contacts", label: "Knows agreed contacts" },
      { key: "unexpected_situations", label: "Responds appropriately to unexpected situations" },
    ],
  },
];

export const SESSION_STATUS_LABELS = {
  scheduled: "Scheduled",
  draft: "Draft",
  submitted: "Submitted",
  cancelled: "Cancelled",
};

export const EXTENSION_REASON_LABELS = {
  additional_training_required: "Additional training required",
  absence: "Absence",
  transport_disruption: "Transport disruption",
  route_change: "Route change",
  child_needs_additional_time: "Child needs additional time",
  risk_review: "Risk review",
  other: "Other",
};

export const PAUSE_REASON_LABELS = {
  child_absence: "Child absence",
  health: "Health",
  school_closure: "School closure",
  family_request: "Family request",
  risk_review: "Risk review",
  transport_disruption: "Transport disruption",
  staffing: "Staffing",
  other: "Other",
};

export const FINAL_REVIEW_DECISIONS = [
  { value: "approve_final_assessment", label: "Approve final assessment" },
  { value: "continue_training", label: "Continue training" },
  { value: "request_additional_sessions", label: "Request additional sessions" },
  { value: "request_risk_review", label: "Request risk review" },
  { value: "pause_programme", label: "Pause programme" },
];
